import { fuego } from '@nandorojo/swr-firestore';

/**
 * HTML 콘텐츠에서 #N 패턴(타임라인 numberID 참조)을 파싱해 고유 숫자 배열로 반환
 * HTML 속성 내부(href="#14", color="#14" 등)는 제외
 */
export function parseTimelineNumberIds(html: string): number[] {
  if (!html) return [];

  // HTML 태그를 제거한 텍스트 기반 탐색:
  // negative lookbehind로 =, ", ', &, # 바로 뒤는 제외 (HTML 속성, 엔티티 방지)
  // negative lookahead로 뒤에 알파벳이 오면 제외 (#hashtag 방지)
  const pattern = /(?<![="'&#])#(\d+)(?!\w)/g;
  const matches = html.matchAll(pattern);

  const ids = Array.from(matches, (m) => parseInt(m[1], 10));
  return Array.from(new Set(ids));
}

/**
 * Firestore에서 collectionPath 컬렉션의 numberId 필드로 docId를 조회
 * 찾으면 docId 문자열 반환, 없으면 null 반환
 */
export async function getPostDocIdByNumberId(
  collectionPath: string,
  numberId: number,
): Promise<string | null> {
  try {
    const snapshot = await fuego.db
      .collection(collectionPath)
      .where('numberId', '==', numberId)
      .limit(1)
      .get();

    if (!snapshot.empty) {
      return snapshot.docs[0].id;
    }
    return null;
  } catch (error) {
    console.error(`[timelineNumberLink] numberId ${numberId} 조회 실패:`, error);
    return null;
  }
}

/**
 * #N 형태의 타임라인 참조 칩 HTML 생성
 * 스타일은 QuillStyle.css의 .timeline-number-chip 클래스로 통일 관리
 */
function generateTimelineChipHTML(
  numberId: number,
  href: string,
): string {
  return `<a href="${href}" class="timeline-number-chip">#${numberId}</a>`;
}

/**
 * HTML 콘텐츠에서 #N 패턴을 찾아 타임라인 게시글 링크 칩으로 치환
 *
 * [신규 모드] #N 패턴을 Firestore 조회 후 칩 HTML로 치환
 * [수정 모드] Quill이 class를 제거/파싱한 기존 칩을 #N 평문으로 원복한 뒤,
 *            새로 추가된 #N 패턴과 함께 일괄 재처리
 * - docId를 찾지 못한 경우 /{collectionPath} (게시판 목록) 으로 fallback
 */
export async function processTimelineNumberTags(
  html: string,
  collectionPath: string,
  isEdit: boolean = false,
): Promise<string> {
  let workHtml = html;

  if (isEdit) {
    // 수정 모드: Quill이 파싱해 class가 사라진 기존 칩(+ 온전한 칩 모두)을 #N 텍스트로 원복
    // href가 /{collectionPath}/... 이고 텍스트가 #숫자인 <a> 태그를 대상으로 함
    workHtml = html.replace(
      new RegExp(
        `<a(?:[^>]*)href="/${collectionPath}/[^"]+"(?:[^>]*)>(#\\d+)<\\/a>`,
        'g',
      ),
      '$1',
    );
  }

  const numberIds = parseTimelineNumberIds(workHtml);
  if (numberIds.length === 0) return workHtml;

  // 각 numberId에 대해 docId 조회 (병렬 처리)
  const docIdMap = new Map<number, string | null>();
  await Promise.all(
    numberIds.map(async (id) => {
      const docId = await getPostDocIdByNumberId(collectionPath, id);
      docIdMap.set(id, docId);
    }),
  );

  // HTML에서 #N 패턴을 칩으로 치환
  const pattern = /(?<![="'&#])#(\d+)(?!\w)/g;
  const result = workHtml.replace(pattern, (_match, numStr) => {
    const numberId = parseInt(numStr, 10);
    const docId = docIdMap.get(numberId);
    const href = docId
      ? `/${collectionPath}/${docId}`
      : `/${collectionPath}`;
    return generateTimelineChipHTML(numberId, href);
  });

  return result;
}
